import { DatePicker } from "@/components/ui/DatePicker";

/**
 * Para pól „od–do" w jednej ramce — przedział czyta się jak jeden filtr, a nie dwa
 * niezależne pola. Puste pole = brak ograniczenia z tej strony; ustawiony przedział
 * podświetla ramkę. Wartości trzyma rodzic (stringi prosto z inputów), bo filtrowanie
 * i tak dzieje się po jego stronie.
 *
 * Klasy inputów/ramki są nadpisywalne, bo listy w aplikacji mają dwie różne palety
 * kontrolek (input-base vs. surowe gray-*) i filtr ma wyglądać jak sąsiedzi.
 */
export interface RangeFilterProps {
  label: string;
  type: "date" | "number";
  from: string;
  to: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
  /** Etykiety dostępnościowe/tooltipy obu pól — np. „Termin oferty od". */
  fromTitle: string;
  toTitle: string;
  /** Krok pola liczbowego (domyślnie 100 — filtry długości chodzą w setkach metrów). */
  step?: number;
  inputClassName?: string;
  /** Klasa ramki w stanie „bez przedziału"; z ustawionym przedziałem ramka zawsze
   *  przechodzi na kolor primary. */
  idleBorderClassName?: string;
}

export function RangeFilter({
  label,
  type,
  from,
  to,
  onFrom,
  onTo,
  fromTitle,
  toTitle,
  step = 100,
  inputClassName,
  idleBorderClassName = "border-[var(--color-border-muted)]",
}: RangeFilterProps) {
  const active = !!(from || to);
  const inputClass =
    inputClassName ??
    (type === "date"
      ? "input-base px-2 py-1 text-xs"
      : "input-base w-[80px] px-2 py-1 text-xs");
  const numberProps = { min: 0, step };
  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 whitespace-nowrap ${
        active ? "border-[var(--color-primary)]" : idleBorderClassName
      }`}
    >
      <span className="text-xs text-[var(--color-text-secondary)]">{label}:</span>
      {/* DatePicker, not a native date input: that one follows the browser language
          (mm/dd/yyyy in an English browser), while the UI always shows dd.mm.yyyy. */}
      {type === "date" ? (
        <div className="w-[140px]" title={fromTitle}>
          <DatePicker value={from} onChange={onFrom} placeholder="dd.mm.rrrr" className={inputClass} aria-label={fromTitle} />
        </div>
      ) : (
        <input
          type={type}
          {...numberProps}
          placeholder="od"
          className={inputClass}
          value={from}
          title={fromTitle}
          aria-label={fromTitle}
          onChange={(e) => onFrom(e.target.value)}
        />
      )}
      <span className="text-xs text-[var(--color-text-muted)]">–</span>
      {type === "date" ? (
        <div className="w-[140px]" title={toTitle}>
          <DatePicker value={to} onChange={onTo} placeholder="dd.mm.rrrr" className={inputClass} aria-label={toTitle} />
        </div>
      ) : (
        <input
          type={type}
          {...numberProps}
          placeholder="do"
          className={inputClass}
          value={to}
          title={toTitle}
          aria-label={toTitle}
          onChange={(e) => onTo(e.target.value)}
        />
      )}
    </div>
  );
}
